// PasswordDialog.jsx — SB-037: the set/change-password surface (extracted from
// AppShell in SB-042). Until SB-037 `api.setPassword` had a wrapper and a backend
// endpoint but NO caller anywhere in the UI — members could not actually set a
// password at all. Doubles as the rotation surface. Owns its form state; the
// parent only controls `open`.
import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import { useT } from '../i18n/index.mjs';

export default function PasswordDialog({ open, onClose }) {
  const t = useT();
  const { account, refresh } = useAuth();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setPw('');
      setPw2('');
      setErr('');
    }
  }, [open]);

  const save = async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (pw.length < 8) { setErr(t('shell.pw.tooShort')); return; }
    if (pw !== pw2) { setErr(t('shell.pw.mismatch')); return; }
    setBusy(true);
    setErr('');
    try {
      await api.setPassword(pw);
      await refresh();
      onClose();
    } catch (e) {
      // 具体到"哪里不合格"才可操作——泛化的"设置失败"会让人反复试同一个弱口令。
      setErr(t(errKey(e, {
        byCode: { COMMON_PASSWORD: 'shell.pw.tooCommon', WEAK_PASSWORD: 'shell.pw.tooShort' },
        fallback: 'shell.pw.error',
      })));
    } finally {
      setBusy(false);
    }
  };

  // 第三百八十六批（需求方 11-02 实撞）：这里原是两个光秃秃的密码框，不在表单里、没有用户名、
  // 不带 autocomplete——浏览器的密码管理器认不出这是「改密码」，不提示更新已存的旧密码；
  // 下次登录页照旧自动填旧密码，服务器按 B3 统一拒绝，读起来只像「密码登录坏了」。
  // 现在是真表单：一个隐藏的只读用户名（autocomplete=username，告诉管理器改的是哪个账号）、
  // 两个 new-password，回车与「保存」都走 submit。
  return (
    <Dialog open={open} onClose={() => (busy ? null : onClose())} fullWidth maxWidth="xs">
      <Box component="form" onSubmit={save} noValidate>
        <DialogTitle>
          {account?.hasPassword ? t('shell.pw.titleChange') : t('shell.pw.titleSet')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('shell.pw.explain')}
          </Typography>
          <input
            type="text" name="username" autoComplete="username"
            value={account?.email ?? ''} readOnly hidden
          />
          <TextField
            autoFocus fullWidth margin="dense" type="password"
            name="new-password" autoComplete="new-password"
            label={t('shell.pw.label')} value={pw}
            onChange={(e) => setPw(e.target.value)} disabled={busy}
            helperText={t('shell.pw.helper')}
          />
          <TextField
            fullWidth margin="dense" type="password"
            name="new-password-confirm" autoComplete="new-password"
            label={t('shell.pw.confirm')} value={pw2}
            onChange={(e) => setPw2(e.target.value)} disabled={busy}
          />
          {err ? <Alert severity="error" sx={{ mt: 1 }}>{err}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={busy}>{t('common.save')}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
